import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CircularProgress, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Autocomplete } from '@material-ui/lab';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { getAzureMapsSearchServices } from 'services';

const useStyles = makeStyles(theme => ({
    icon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(2)
    }
}));

const normalizeAzureAddress = result => {
    const address = result?.address || {};
    const streetAddress = [address.streetNumber, address.streetName]
        .filter(Boolean)
        .join(' ')
        .trim();

    return {
        address: streetAddress || address.freeformAddress || '',
        city: address.municipality || address.municipalitySubdivision || '',
        state: address.countrySubdivisionCode || address.countrySubdivision || '',
        stateCode: address.countrySubdivisionCode || '',
        stateName: address.countrySubdivisionName || address.countrySubdivision || '',
        country: address.countryCode || '',
        zip: address.postalCode || '',
        rawResult: result
    };
};

const normalizeText = value => String(value ?? '').trim().toLowerCase();

export const findStateCode = (stateList, selectedAddress) => {
    if (!Array.isArray(stateList) || stateList.length === 0) {
        return '';
    }

    const candidates = [
        selectedAddress?.stateCode,
        selectedAddress?.stateName,
        selectedAddress?.state
    ].filter(Boolean);

    for (const candidate of candidates) {
        const match = stateList.find(
            option =>
                normalizeText(option?.code) === normalizeText(candidate) ||
                normalizeText(option?.state) === normalizeText(candidate)
        );

        if (match) {
            return match.code;
        }
    }

    return '';
};

export const useAzureMapsStateResolver = () => {
    const stateListForCountries = useSelector(state => state.common?.stateListForCountries);
    const pendingRef = useRef([]);

    const applyState = useCallback((request, stateList) => {
        const code = findStateCode(stateList, request.selectedAddress);

        if (code) {
            request.setFieldValue(request.fieldPath, code, true);
            request.onResolved && request.onResolved(code);
        }
    }, []);

    const resolveState = useCallback(
        request => {
            if (!request?.setFieldValue || !request?.fieldPath || !request?.countryCode) {
                return;
            }

            const stateList = stateListForCountries?.[request.countryCode];

            if (Array.isArray(stateList) && stateList.length > 0) {
                applyState(request, stateList);
                return;
            }

            // The list for a newly selected country is still loading; resolve once it arrives.
            pendingRef.current = [
                ...pendingRef.current.filter(item => item.fieldPath !== request.fieldPath),
                request
            ];
        },
        [stateListForCountries, applyState]
    );

    useEffect(() => {
        if (pendingRef.current.length === 0) {
            return;
        }

        pendingRef.current = pendingRef.current.filter(item => {
            const stateList = stateListForCountries?.[item.countryCode];

            if (!Array.isArray(stateList) || stateList.length === 0) {
                return true;
            }

            applyState(item, stateList);
            return false;
        });
    }, [stateListForCountries, applyState]);

    return resolveState;
};

const AzureMapsAddressSearch = ({
    countrySet,
    debounceMs,
    disabled,
    label,
    language,
    limit,
    minLength,
    onAddressSelect,
    onBlur,
    onInputChange,
    inputProps,
    value,
}) => {
    const classes = useStyles();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);
    const abortControllerRef = useRef(null);

    const getOptionLabel = option => {
        if (typeof option === 'string') {
            return option;
        }

        return option?.address?.freeformAddress || '';
    };

    const fetchSuggestions = async query => {
        if (!query || query.length < minLength) {
            setOptions([]);
            setLoading(false);
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);

        try {
            const result = await getAzureMapsSearchServices({
                query,
                limit: String(limit),
                language,
                typeahead: 'true',
                ...(countrySet ? { countrySet } : {})
            }, controller.signal);

            setOptions(Array.isArray(result?.results) ? result.results : []);
        } catch (error) {
            if (error.name !== 'AbortError') {
                setOptions([]);
            }
        } finally {
            if (abortControllerRef.current === controller) {
                setLoading(false);
            }
        }
    };

    const handleInputChange = (_, newInputValue, reason) => {
        if (reason !== 'reset') {
            onInputChange && onInputChange(newInputValue, reason);
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        if (reason !== 'input') {
            if (!newInputValue || newInputValue.length < minLength) {
                setOptions([]);
            }
            return;
        }

        timerRef.current = setTimeout(() => {
            fetchSuggestions(newInputValue);
        }, debounceMs);
    };

    const handleChange = (_, selectedOption) => {
        if (!selectedOption || typeof selectedOption === 'string') {
            return;
        }

        onAddressSelect && onAddressSelect(normalizeAzureAddress(selectedOption));
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const autocompleteIdRef = useRef(`azure-maps-address-search-${Math.random().toString(36).slice(2)}`);

    return (
        <Autocomplete
            id={autocompleteIdRef.current}
            fullWidth
            autoComplete
            freeSolo
            includeInputInList
            options={options}
            filterOptions={x => x}
            disabled={disabled}
            inputValue={value || ''}
            onInputChange={handleInputChange}
            onChange={handleChange}
            getOptionLabel={getOptionLabel}
            renderInput={params => (
                <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label={label}
                    onBlur={onBlur}
                    inputProps={{
                        ...params.inputProps,
                        ...inputProps
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
            renderOption={option => {
                const freeformAddress = option?.address?.freeformAddress || '';
                const secondaryText = [
                    option?.address?.municipality,
                    option?.address?.countrySubdivision,
                    option?.address?.postalCode,
                    option?.address?.countryCode
                ]
                    .filter(Boolean)
                    .join(', ');

                return (
                    <Grid container alignItems="center">
                        <Grid item>
                            <LocationOnIcon className={classes.icon} />
                        </Grid>
                        <Grid item xs>
                            {freeformAddress}
                            <Typography variant="body2" color="textSecondary">
                                {secondaryText}
                            </Typography>
                        </Grid>
                    </Grid>
                );
            }}
        />
    );
};

AzureMapsAddressSearch.defaultProps = {
    countrySet: '',
    debounceMs: 300,
    disabled: false,
    language: 'en-US',
    limit: 5,
    minLength: 3,
    onAddressSelect: null,
    onBlur: null,
    onInputChange: null,
    inputProps: {},
    value: ''
};

AzureMapsAddressSearch.propTypes = {
    countrySet: PropTypes.string,
    debounceMs: PropTypes.number,
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    language: PropTypes.string,
    limit: PropTypes.number,
    minLength: PropTypes.number,
    onAddressSelect: PropTypes.func,
    onBlur: PropTypes.func,
    onInputChange: PropTypes.func,
    inputProps: PropTypes.object,
    value: PropTypes.string
};

export default AzureMapsAddressSearch;
